/**
 * Hooks de TanStack Query para fetching tipado.
 *
 * Centraliza las query keys y opciones por endpoint. Los componentes
 * importan `useProductos`, `useRemitos`, etc. y obtienen data + loading
 * + error tipados sin tocar el cliente HTTP.
 *
 * Cuando Eze conecte el back real, las query keys y firmas se mantienen.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  catalogosApi,
  seriesApi,
  remitosApi,
  operacionesApi,
  ubicacionesApi,
  eventosAnmatApi,
  despachoApi,
  recepcionApi,
  ingresoApi,
  syncApi,
  anmatOpsApi,
  tareasApi,
  comprobantesApi,
  anmatStatusApi,
  trazaApi,
  despachoCompletadasApi,
  clientesAcsisApi,
  auditLogApi,
  recepcionRealApi,
  erpTaskApi,
  reingresosApi,
  pickeoLogApi,
} from '@/api/endpoints';
import type { Producto } from '@/api/contracts/productos';
import type { Cliente } from '@/api/contracts/clientes';
import type { Serie } from '@/api/contracts/series';
import type { Remito } from '@/api/contracts/remitos';
import type { Operacion, Movimiento } from '@/api/contracts/operaciones';
import type { Ubicacion } from '@/api/contracts/ubicaciones';
import type { EventoAnmat } from '@/api/contracts/eventos-anmat';
import type { TareaPendiente } from '@/api/contracts/tareas';
import type { ComprobanteDetalle } from '@/api/contracts/comprobantes';
import type { ComprobanteTipo } from '@/api/endpoints/comprobantes';
import { consultarGlnSnt } from '@/api/endpoints/snt';
import type { AnmatStatus } from '@/api/contracts/anmat-status';
import type { TrazaJobDetalle } from '@/api/contracts/traza-job-detalle';
import type { TrazaCompletada } from '@/api/contracts/despacho-completadas';
import type { Cliente as ClienteAcsis } from '@/api/contracts/cliente';
import type {
  ConfirmarDespachoRequest,
  ConfirmarDespachoResponse,
  CancelarTransaccionRequest,
  CancelarTransaccionResponse,
} from '@/api/endpoints/despacho';
import type {
  ConfirmarRecepcionRequest,
  ConfirmarRecepcionResponse,
  CancelarRecepcionRequest,
  CancelarRecepcionResponse,
} from '@/api/endpoints/recepcion';
import type {
  SincronizarRequest,
  SincronizarResponse,
  BuscarRemitoRequest,
  BuscarRemitoResponse,
} from '@/api/endpoints/sync';
import type {
  ReintentarAnmatRequest,
  ReintentarAnmatResponse,
  EditarOperacionRequest,
  EditarOperacionResponse,
} from '@/api/endpoints/anmat-ops';
import type {
  CrearIngresoRequest,
  CrearIngresoResponse,
  EliminarIngresoRequest,
  EliminarIngresoResponse,
} from '@/api/endpoints/ingreso';

// Query keys centralizadas para que las invalidations sean explícitas
export const queryKeys = {
  ubicaciones: ['ubicaciones'] as const,
  productos: ['productos'] as const,
  clientes: ['clientes'] as const,
  series: ['series'] as const,
  remitos: ['remitos'] as const,
  operaciones: ['operaciones'] as const,
  movimientos: ['movimientos'] as const,
  eventosAnmat: ['eventos-anmat'] as const,
  tareasPendientes: (glnId: string | number) =>
    ['tareas-pendientes', String(glnId)] as const,
  comprobante: (referencia: string, tipo: ComprobanteTipo) =>
    ['comprobante', tipo, referencia] as const,
  comprobanteRaw: (referencia: string, tipo: ComprobanteTipo) =>
    ['comprobante-raw', tipo, referencia] as const,
  previewPayloadEgreso: (referencia: string, tipo: ComprobanteTipo, glnId: string | number) =>
    ['preview-payload-egreso', tipo, referencia, String(glnId)] as const,
  trazaStats: (glnId: string | number) => ['traza-stats', String(glnId)] as const,
  anmatStatus: ['anmat-status'] as const,
  trazaJob: (referencia: string, tipo: trazaApi.TrazaJobTipo) =>
    ['traza-job', tipo, referencia] as const,
  despachoCompletadas: ['despacho-completadas'] as const,
  clienteAcsis: (id: string) => ['cliente-acsis', id] as const,
  auditEntries: (filters: Record<string, unknown>) => ['audit-entries', filters] as const,
  auditVerify: ['audit-verify'] as const,
  recepcionPendientes: (
    glnId: string | number,
    maxRemitos: number,
    remito: string,
    factura: string,
  ) => ['recepcion-pendientes', String(glnId), maxRemitos, remito, factura] as const,
  recepcionDetalle: (
    glnId: string | number,
    numeroRemito: string | null,
    numeroFactura: string | null,
  ) => ['recepcion-detalle', String(glnId), numeroRemito, numeroFactura] as const,
  erpTasks: (glnId: string | number, estado: string) =>
    ['erp-tasks', String(glnId), estado] as const,
  erpTaskLastSync: (glnId: string | number) =>
    ['erp-task-last-sync', String(glnId)] as const,
};

// staleTime largo para catálogos (rara vez cambian)
const CATALOG_STALE_TIME = 5 * 60 * 1000; // 5 min

export function useUbicaciones(): UseQueryResult<Ubicacion[]> {
  return useQuery({
    queryKey: queryKeys.ubicaciones,
    queryFn: ubicacionesApi.getUbicaciones,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useProductos(): UseQueryResult<Producto[]> {
  return useQuery({
    queryKey: queryKeys.productos,
    queryFn: catalogosApi.getProductos,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useClientes(): UseQueryResult<Cliente[]> {
  return useQuery({
    queryKey: queryKeys.clientes,
    queryFn: catalogosApi.getClientes,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useSeries(): UseQueryResult<Serie[]> {
  return useQuery({
    queryKey: queryKeys.series,
    queryFn: seriesApi.getSeries,
  });
}

export function useRemitos(): UseQueryResult<Remito[]> {
  return useQuery({
    queryKey: queryKeys.remitos,
    queryFn: remitosApi.getRemitos,
  });
}

export function useOperaciones(): UseQueryResult<Operacion[]> {
  return useQuery({
    queryKey: queryKeys.operaciones,
    queryFn: operacionesApi.getOperaciones,
  });
}

export function useMovimientos(): UseQueryResult<Movimiento[]> {
  return useQuery({
    queryKey: queryKeys.movimientos,
    queryFn: operacionesApi.getMovimientos,
  });
}

export function useEventosAnmat(): UseQueryResult<EventoAnmat[]> {
  return useQuery({
    queryKey: queryKeys.eventosAnmat,
    queryFn: eventosAnmatApi.getEventosAnmat,
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * Tareas pendientes de trazar (polling al ERP ACSIS, backend real).
 *
 * `refetchInterval` dinámico:
 *   - Si hay alguna tarea con traza en curso (pending/running) → 3s.
 *     Una traza típica tarda ~5s, así enseguida vemos el chip pasar a OK.
 *   - Caso normal → 30s. Solo nos interesa enterarnos de tareas nuevas
 *     generadas por Plataforma (no es algo que cambie cada segundo).
 *
 * Sin refetchIntervalInBackground: si el usuario va a otra pestaña, no
 * spameamos ACSIS por nada.
 */
export function useTareasPendientes(
  glnId: string | number | null,
): UseQueryResult<TareaPendiente[]> {
  return useQuery({
    queryKey: glnId ? queryKeys.tareasPendientes(glnId) : ['tareas-pendientes', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return tareasApi.getTareasPendientes(glnId);
    },
    enabled: Boolean(glnId),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!Array.isArray(data)) return 30_000;
      const hayTrazaEnCurso = data.some(
        (t) => t.traza?.status === 'pending' || t.traza?.status === 'running',
      );
      return hayTrazaEnCurso ? 3_000 : 30_000;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Detalle de un comprobante (lazy: solo se dispara si hay `referencia` + `tipo`).
 * Pega a /api/despacho/comprobante/:referencia?tipo=... — devuelve header + items
 * + partidas (con N°serie / lote / vencimiento).
 */
/**
 * Health check del SNT (ANMAT). Refresca cada 10s para que el indicador del
 * header muestre estado en vivo. `retry: false` evita ráfagas si el backend
 * o riga están caídos — el siguiente intervalo intenta de nuevo.
 */
export function useAnmatStatus(): UseQueryResult<AnmatStatus> {
  return useQuery({
    queryKey: queryKeys.anmatStatus,
    queryFn: anmatStatusApi.getAnmatStatus,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  });
}

/**
 * Marca un GLN como default del usuario autenticado.
 * El backend hace UPDATE en `user_gln.is_default` (NULL para el viejo, 1
 * para el nuevo) atómicamente. No invalidamos `useUbicaciones` porque el
 * listado de GLN no cambió — el componente UI lee el default vía
 * `usuario.ubicacionDefaultId` del session store, no del listado.
 */
export function useSetDefaultGlnMutation() {
  return useMutation({
    mutationFn: (glnId: string) => ubicacionesApi.setDefaultGln(glnId),
  });
}

/**
 * Inicia una traza de egreso para un comprobante. El backend hace POST a
 * riga-traza-v2 con el payload HARDCODED por ahora; al volver, devuelve el
 * job_id. Tras el éxito, invalidamos las tareas pendientes para que la card
 * refleje el nuevo estado (chip "Trazando").
 */
export function useIniciarTrazaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      referencia,
      tipo,
      glnId,
      erpId,
      idEvento,
      eventoDesc,
    }: {
      referencia: string;
      tipo: trazaApi.IniciarTrazaTipo;
      glnId: string | number;
      /** Id del registro de la tarea en ACSIS (tarea.id de tareas.json).
       *  Se persiste en `traza_job.erp_id` para correlación con el ERP. */
      erpId: string | null;
      /** event_code ANMAT elegido en el combo. Opcional (fallback en backend). */
      idEvento?: number | null;
      /** Descripción del evento — snapshot para mostrar el despacho trazado. */
      eventoDesc?: string | null;
    }) => trazaApi.iniciarTrazaEgreso(referencia, tipo, glnId, erpId, idEvento, eventoDesc),
    onSuccess: (_data, variables) => {
      // Invalida TODAS las tareas-pendientes (sea cual sea el glnId) — la card
      // que esté visible va a refetchear con su propio glnId.
      void qc.invalidateQueries({ queryKey: ['tareas-pendientes'] });
      void qc.invalidateQueries({
        queryKey: queryKeys.trazaJob(variables.referencia, 'egreso'),
      });
    },
  });
}

/**
 * Mutation: re-dispara el cierre de tarea en Plataforma para una referencia
 * ya trazada (reintento / test). No invalida nada del listado — el estado de
 * la traza no cambia, solo se cierra la tarea en el ERP.
 */
export function useCumplirTareaMutation() {
  return useMutation({
    mutationFn: (referencia: string) => trazaApi.cumplirTareaErp(referencia),
  });
}

/** Estados terminales de un job — el polling se autopara al llegar a uno de estos. */
const TRAZA_TERMINAL_STATUS = new Set(['success', 'error', 'failed', 'anulada']);

/**
 * Cliente de ACSIS — destino del despacho. Solo dispara si hay `id`.
 * Stale-while-revalidate: como el cliente cambia poco, no hay refetch interval.
 */
export function useClienteAcsis(id: string | null): UseQueryResult<ClienteAcsis> {
  return useQuery({
    queryKey: id ? queryKeys.clienteAcsis(id) : ['cliente-acsis', 'idle'],
    queryFn: () => {
      if (!id) throw new Error('id requerido');
      return clientesAcsisApi.getCliente(id);
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Consulta en vivo de un GLN contra el SNT (ANMAT). `data` es el agente
 * registrado, o `null` si el SNT no lo encuentra. `error` = falla del SNT.
 * Se usa para el "Tipo agente SNT" del despacho y la pantalla de consulta.
 */
export function useGlnSnt(gln: string | null, enabled = true) {
  return useQuery({
    queryKey: ['gln-snt', gln],
    queryFn: () => consultarGlnSnt(gln as string),
    enabled: Boolean(gln) && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Trazas de egreso completadas con éxito. Refresca cada 30s para reflejar
 * jobs que el poller del backend acaba de marcar como success.
 */
export function useDespachoCompletadas(): UseQueryResult<TrazaCompletada[]> {
  return useQuery({
    queryKey: queryKeys.despachoCompletadas,
    queryFn: despachoCompletadasApi.getDespachoCompletadas,
    refetchInterval: 30_000,
  });
}

/**
 * Lee la última fila de `traza_job` para una (referencia, tipo) — solo
 * backend, no toca riga.
 *
 * Polling solo cuando tiene sentido:
 *   - Sin traza todavía (data=null) → NO polling. La mutation que dispara
 *     "Trazar" hace invalidate al terminar, así que el hook se entera del
 *     primer job sin necesidad de estar consultando.
 *   - Traza en `pending/running`     → refresca cada 1s (queremos ver el
 *     status pasar a OK lo más rápido posible).
 *   - Traza en estado terminal       → NO polling (ya no cambia).
 */
export function useTrazaJobByReferencia(
  referencia: string | null,
  tipo: trazaApi.TrazaJobTipo,
): UseQueryResult<TrazaJobDetalle | null> {
  return useQuery({
    queryKey: referencia ? queryKeys.trazaJob(referencia, tipo) : ['traza-job', 'idle'],
    queryFn: () => {
      if (!referencia) throw new Error('referencia requerida');
      return trazaApi.getTrazaJobByReferencia(referencia, tipo);
    },
    enabled: Boolean(referencia),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false; // sin traza todavía → no consultar
      if (TRAZA_TERMINAL_STATUS.has(data.status)) return false;
      return 1000;
    },
    refetchIntervalInBackground: false,
    retry: false,
  });
}

export function useComprobante(
  referencia: string | null,
  tipo: ComprobanteTipo | null,
): UseQueryResult<ComprobanteDetalle> {
  return useQuery({
    queryKey: referencia && tipo ? queryKeys.comprobante(referencia, tipo) : ['comprobante', 'idle'],
    queryFn: () => {
      if (!referencia || !tipo) {
        throw new Error('Referencia y tipo requeridos');
      }
      return comprobantesApi.getComprobante(referencia, tipo);
    },
    enabled: Boolean(referencia && tipo),
  });
}

/**
 * Stats del KPI del dashboard para el GLN activo (errores ANMAT).
 * Refresca cada 30s; se silencia en background.
 */
export function useTrazaStats(
  glnId: string | number | null,
): UseQueryResult<trazaApi.TrazaStats> {
  return useQuery({
    queryKey: glnId ? queryKeys.trazaStats(glnId) : ['traza-stats', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return trazaApi.getTrazaStats(glnId);
    },
    enabled: Boolean(glnId),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    // Refetch SIEMPRE al volver al dashboard (ignora el staleTime global de
    // 30s). El operador espera ver el KPI actualizado al entrar.
    refetchOnMount: 'always',
  });
}

/**
 * Lazy: preview del payload que se le mandaría a riga-traza para una traza
 * de egreso. Se dispara solo cuando `enabled` es true (modal abierto).
 * NO toca riga ni persiste nada — es debug puro.
 */
export function usePreviewPayloadEgreso(
  referencia: string | null,
  tipo: ComprobanteTipo | null,
  glnId: string | number | null,
  idEvento: number | null,
  enabled: boolean,
): UseQueryResult<unknown> {
  return useQuery({
    queryKey:
      referencia && tipo && glnId
        ? [...queryKeys.previewPayloadEgreso(referencia, tipo, glnId), idEvento]
        : ['preview-payload-egreso', 'idle'],
    queryFn: () => {
      if (!referencia || !tipo || !glnId) throw new Error('Faltan parámetros');
      return trazaApi.previewPayloadEgreso(referencia, tipo, glnId, idEvento);
    },
    enabled: enabled && Boolean(referencia && tipo && glnId),
    staleTime: 30 * 1000,
  });
}

/**
 * Eventos ANMAT disponibles para trazar el egreso de una referencia, según el
 * par (origen = GLN activo, destino = GLN destino del comprobante). Alimenta el
 * combo "Evento SNT" del detalle. Consulta el SNT en vivo por detrás, así que
 * se cachea unos minutos. Se dispara solo con referencia + glnId + `enabled`.
 */
export function useEventosDisponibles(
  referencia: string | null,
  glnId: string | number | null,
  glnDestino: string | null,
  enabled = true,
): UseQueryResult<trazaApi.EventosDisponiblesResult> {
  return useQuery({
    // `glnDestino` va en la key (no en el request: el backend lo lee de la DB)
    // solo para que la lista se re-consulte si se edita el GLN destino.
    queryKey: [
      'eventos-disponibles',
      referencia,
      glnId ? String(Number(glnId)) : null,
      glnDestino,
    ],
    queryFn: () => {
      if (!referencia || !glnId) throw new Error('Faltan parámetros');
      return trazaApi.getEventosDisponibles(referencia, glnId);
    },
    enabled: enabled && Boolean(referencia && glnId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Lazy: el modal "ver log" del detalle dispara este hook solo cuando se abre.
 * Devuelve el JSON crudo de ACSIS para auditoría/diagnóstico.
 */
export function useComprobanteRaw(
  referencia: string | null,
  tipo: ComprobanteTipo | null,
  enabled: boolean,
): UseQueryResult<comprobantesApi.ComprobanteRaw> {
  return useQuery({
    queryKey:
      referencia && tipo ? queryKeys.comprobanteRaw(referencia, tipo) : ['comprobante-raw', 'idle'],
    queryFn: () => {
      if (!referencia || !tipo) throw new Error('Referencia y tipo requeridos');
      return comprobantesApi.getComprobanteRaw(referencia, tipo);
    },
    enabled: enabled && Boolean(referencia && tipo),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation: vincula el stock del despacho a product_var (backfill por SKU,
 * creando el product_var desde el ERP si falta). Invalida el detalle al éxito.
 */
export function useVincularProductVarMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      referencia: string;
      tipo: ComprobanteTipo;
    }) => comprobantesApi.vincularProductVar(args.referencia),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
    },
  });
}

/**
 * Mutation: resync liviano del despacho (gln_destino + GTIN/product_var).
 * No toca stock. Invalida el detalle al éxito para reflejar los datos nuevos.
 */
export function useResincronizarDespachoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { referencia: string; tipo: ComprobanteTipo }) =>
      comprobantesApi.resincronizarDespacho(args.referencia),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
    },
  });
}

/**
 * Chequeo read-only de cambios contra el ERP (previo a trazar). No invalida
 * nada — solo consulta ACSIS y devuelve si el remito cambió.
 */
export function useVerificarCambiosMutation() {
  return useMutation({
    mutationFn: (referencia: string) => comprobantesApi.verificarCambiosDespacho(referencia),
  });
}

/** Carga manual del GLN destino de un despacho. Refresca el detalle al guardar. */
export function useSetGlnDestinoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { referencia: string; tipo: ComprobanteTipo; glnDestino: string }) =>
      comprobantesApi.setGlnDestino(args.referencia, args.glnDestino),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
    },
  });
}

/** Series de un despacho reservadas por otros remitos (candidatas a reasignación). */
export function useReasignables(referencia: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['reasignables', referencia],
    queryFn: () => comprobantesApi.getReasignables(referencia as string),
    enabled: Boolean(referencia) && enabled,
    staleTime: 0,
  });
}

/** Reasigna a este despacho las series reservadas por otros remitos. */
export function useReasignarSeriesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { referencia: string; tipo: ComprobanteTipo }) =>
      comprobantesApi.reasignarSeries(args.referencia),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
      void qc.invalidateQueries({ queryKey: ['reasignables', variables.referencia] });
    },
  });
}

/** Toma para este despacho las series en inventario libre (a=1). */
export function useAsignarDisponiblesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { referencia: string; tipo: ComprobanteTipo }) =>
      comprobantesApi.asignarDisponibles(args.referencia),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
      void qc.invalidateQueries({ queryKey: ['tareas-pendientes'] });
    },
  });
}

/**
 * Pickeo por escaneo: registrar y cancelar una unidad. No invalidan el
 * comprobante: el estado del pickeo lo lleva `usePickeoLotes` y se reconcilia
 * con el back al volver a cargar el detalle.
 */
export function useRegistrarPickeoMutation() {
  return useMutation({
    mutationFn: (args: { referencia: string; gtin: string; lote: string; serie: string }) =>
      comprobantesApi.registrarPickeo(args.referencia, {
        gtin: args.gtin,
        lote: args.lote,
        serie: args.serie,
      }),
  });
}

export function useCancelarPickeoMutation() {
  return useMutation({
    mutationFn: (args: { referencia: string; stockId: number }) =>
      comprobantesApi.cancelarPickeo(args.referencia, args.stockId),
  });
}

/** Paso 1 de la anulación: cancelar egreso (o→r). */
export function useCancelarEgresoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      referencia: string;
      tipo: ComprobanteTipo;
      destino: comprobantesApi.DestinoAnulacionEgreso;
    }) => comprobantesApi.cancelarEgreso(args.referencia, args.destino),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
      void qc.invalidateQueries({ queryKey: queryKeys.trazaJob(variables.referencia, 'egreso') });
      void qc.invalidateQueries({ queryKey: queryKeys.despachoCompletadas });
      // `retrazar` devuelve el remito a pendientes; `en_transito` suma unidades
      // pendientes de reingreso.
      void qc.invalidateQueries({ queryKey: ['tareas-pendientes'] });
      void qc.invalidateQueries({ queryKey: ['reingresos-pendientes'] });
    },
  });
}

/** Paso 2 de la anulación: cancelar remito (r→a). */
export function useCancelarRemitoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { referencia: string; tipo: ComprobanteTipo }) =>
      comprobantesApi.cancelarRemito(args.referencia),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['comprobante', variables.tipo, variables.referencia],
      });
      void qc.invalidateQueries({ queryKey: queryKeys.despachoCompletadas });
    },
  });
}

/**
 * Log del cierre de tarea en el ERP (request/response desde audit_log).
 * Solo dispara cuando hay `erpId` y `enabled` (ej. modal abierto).
 */
export function useCumplirTareaLog(
  erpId: string | null,
  enabled: boolean,
): UseQueryResult<comprobantesApi.CumplirTareaLogEntry[]> {
  return useQuery({
    queryKey: ['cumplir-tarea-log', erpId],
    queryFn: () => {
      if (!erpId) throw new Error('erpId requerido');
      return comprobantesApi.getCumplirTareaLog(erpId);
    },
    enabled: enabled && Boolean(erpId),
    staleTime: 10_000,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Confirmar despacho. Tras éxito invalida remitos + series + operaciones +
 * movimientos para que las vistas se re-fetcheen.
 *
 * El timer del SNT (2s) dispara desde el endpoint mock — para ver el cambio
 * de estado de en_cola→informada hay que volver a abrir/refetchear.
 */
export function useConfirmarDespachoMutation(): UseMutationResult<
  ConfirmarDespachoResponse,
  Error,
  ConfirmarDespachoRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: despachoApi.confirmarDespacho,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.remitos });
      void qc.invalidateQueries({ queryKey: queryKeys.series });
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useCancelarTransaccionMutation(): UseMutationResult<
  CancelarTransaccionResponse,
  Error,
  CancelarTransaccionRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: despachoApi.cancelarTransaccion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.remitos });
      void qc.invalidateQueries({ queryKey: queryKeys.series });
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useConfirmarRecepcionMutation(): UseMutationResult<
  ConfirmarRecepcionResponse,
  Error,
  ConfirmarRecepcionRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recepcionApi.confirmarRecepcion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.series });
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useCancelarRecepcionMutation(): UseMutationResult<
  CancelarRecepcionResponse,
  Error,
  CancelarRecepcionRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recepcionApi.cancelarRecepcion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.series });
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useCrearIngresoMutation(): UseMutationResult<
  CrearIngresoResponse,
  Error,
  CrearIngresoRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ingresoApi.crearIngreso,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.series });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useEliminarIngresoMutation(): UseMutationResult<
  EliminarIngresoResponse,
  Error,
  EliminarIngresoRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ingresoApi.eliminarIngreso,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.series });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useSincronizarMutation(): UseMutationResult<
  SincronizarResponse,
  Error,
  SincronizarRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: syncApi.sincronizarConErp,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.remitos });
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
    },
  });
}

export function useBuscarRemitoMutation(): UseMutationResult<
  BuscarRemitoResponse,
  Error,
  BuscarRemitoRequest
> {
  return useMutation({
    mutationFn: syncApi.buscarRemitoEnSnt,
  });
}

export function useReintentarAnmatMutation(): UseMutationResult<
  ReintentarAnmatResponse,
  Error,
  ReintentarAnmatRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: anmatOpsApi.reintentarAnmat,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
    },
  });
}

export function useEditarOperacionMutation(): UseMutationResult<
  EditarOperacionResponse,
  Error,
  EditarOperacionRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: anmatOpsApi.editarOperacion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.operaciones });
      void qc.invalidateQueries({ queryKey: queryKeys.movimientos });
      void qc.invalidateQueries({ queryKey: queryKeys.series });
    },
  });
}

/**
 * Listado de remitos ANMAT pendientes de confirmar (recepción).
 * Llega del backend que hace GET a riga-traza-v2 → ANMAT
 * (transacciones-no-confirmadas-agrupadas). 1 card == 1 remito.
 *
 * Refresca cada 60s — los pendientes cambian al ritmo que otros eslabones
 * de la cadena traten egresos hacia este GLN, no es algo de segundos.
 */
export function useRecepcionPendientes(
  glnId: string | number | null,
  maxRemitos: number,
  filtros: { remito?: string; factura?: string } = {},
  habilitado = true,
): UseQueryResult<import('@/api/contracts/recepcion-pendientes').GetRecepcionPendientes> {
  const remito = filtros.remito ?? '';
  const factura = filtros.factura ?? '';
  return useQuery({
    queryKey: glnId
      ? queryKeys.recepcionPendientes(glnId, maxRemitos, remito, factura)
      : ['recepcion-pendientes', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return recepcionRealApi.getRecepcionPendientes(glnId, { maxRemitos, remito, factura });
    },
    // `habilitado` permite desactivar la búsqueda automática al montar (la
    // pantalla de recepción no busca al entrar; recién cuando el usuario lo pide).
    enabled: Boolean(glnId) && habilitado,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  });
}

/**
 * Confirma todas las transacciones pendientes de un remito (o factura).
 * Backend hace POST a riga `/api/in/trazar-pendientes` (sincrónico). Al
 * terminar invalidamos pendientes (la operación ya no debe aparecer) y el
 * detalle (la próxima consulta debería decir "no encontró" porque ya fue
 * confirmada).
 */
export function useConfirmarIngresoMutation(): UseMutationResult<
  import('@/api/contracts/recepcion-detalle').ConfirmarIngresoResponse,
  Error,
  import('@/api/endpoints/recepcion-pendientes').ConfirmarIngresoRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recepcionRealApi.confirmarIngreso,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recepcion-pendientes'] });
      void qc.invalidateQueries({ queryKey: ['recepcion-detalle'] });
    },
  });
}

/**
 * Detalle de una operación ANMAT — el backend intenta por remito y, si no
 * encuentra, hace fallback a factura. Devuelve items + cómo se encontró +
 * log de cada intento.
 */
/**
 * Listado de tareas locales `erp_task` (sincronizadas desde ACSIS).
 * Default: estado='pendiente'. Sin refetchInterval — el sync es manual,
 * el dato se actualiza cuando el operador clickea "Sincronizar".
 */
export function useErpTasks(
  glnId: string | number | null,
  estado: string = 'pendiente',
): UseQueryResult<import('@/api/endpoints/erp-task').ListErpTasksResponse> {
  return useQuery({
    queryKey: glnId ? queryKeys.erpTasks(glnId, estado) : ['erp-tasks', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return erpTaskApi.listErpTasks(glnId, { estado });
    },
    enabled: Boolean(glnId),
    staleTime: 60_000,
    // Refetch al volver a la pantalla (dashboard / listado) — ignora staleTime
    // global. Garantiza que el operador vea el conteo fresco al entrar.
    refetchOnMount: 'always',
  });
}

/**
 * Última vez que se sincronizó exitosamente el GLN — leído de audit_log.
 * Se usa al lado del botón Sincronizar para que el dato persista entre
 * sesiones (no depende del estado en memoria del front).
 */
export function useLastErpTaskSync(
  glnId: string | number | null,
): UseQueryResult<import('@/api/endpoints/erp-task').LastSyncResponse> {
  return useQuery({
    queryKey: glnId ? queryKeys.erpTaskLastSync(glnId) : ['erp-task-last-sync', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return erpTaskApi.getLastErpTaskSync(glnId);
    },
    enabled: Boolean(glnId),
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
}

/**
 * Si el back tiene habilitada la ingesta manual de JSON (ERP_INGESTA_MANUAL).
 * Decide si el header muestra el botón "Ingesta de JSON"; el back igual
 * rechaza la ingesta si está apagada.
 */
export function useIngestaManualHabilitada(
  enabled: boolean,
): UseQueryResult<import('@/api/endpoints/erp-task').IngestaManualEstado> {
  return useQuery({
    queryKey: ['erp-task-ingesta-manual'],
    queryFn: () => erpTaskApi.getIngestaManual(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Ingesta manual de un JSON de detalle: refresca los pendientes al terminar. */
export function useIngestaManualMutation(): UseMutationResult<
  import('@/api/endpoints/erp-task').IngestaManualResult,
  Error,
  { glnId: string | number; detalle: unknown }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ glnId, detalle }) => erpTaskApi.ingestarDetalleManual(glnId, detalle),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['erp-tasks'] });
      void qc.invalidateQueries({ queryKey: ['tareas-pendientes'] });
    },
  });
}

/**
 * Unidades pendientes de reingreso físico (egreso anulado "en tránsito") del
 * GLN activo. Sin refetch automático: el listado cambia cuando alguien anula
 * un egreso o confirma un reingreso.
 */
export function useReingresosPendientes(
  glnId: string | number | null,
): UseQueryResult<import('@/api/contracts/reingresos').ReingresoPendiente[]> {
  return useQuery({
    queryKey: glnId ? ['reingresos-pendientes', String(glnId)] : ['reingresos-pendientes', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return reingresosApi.getReingresosPendientes(glnId);
    },
    enabled: Boolean(glnId),
    refetchOnMount: 'always',
  });
}

/** Confirma el reingreso físico de unidades (na → a). Refresca el listado. */
export function useConfirmarReingresoMutation(): UseMutationResult<
  import('@/api/contracts/reingresos').ConfirmarReingresoResult,
  Error,
  { glnId: string | number; stockIds: readonly number[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ glnId, stockIds }) => reingresosApi.confirmarReingresos(glnId, stockIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reingresos-pendientes'] });
    },
  });
}

export function useRecepcionDetalle(
  glnId: string | number | null,
  numeroRemito: string | null,
  numeroFactura: string | null,
): UseQueryResult<import('@/api/contracts/recepcion-detalle').RecepcionDetalle> {
  const hasFiltro = Boolean(numeroRemito || numeroFactura);
  return useQuery({
    queryKey:
      glnId && hasFiltro
        ? queryKeys.recepcionDetalle(glnId, numeroRemito, numeroFactura)
        : ['recepcion-detalle', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return recepcionRealApi.getRecepcionDetalle(glnId, { numeroRemito, numeroFactura });
    },
    enabled: Boolean(glnId && hasFiltro),
    staleTime: 60_000,
  });
}

/**
 * Listado admin de usuarios — backend `/api/users` (admin-only).
 */
export function useUsuariosAdmin(): UseQueryResult<
  import('@/api/contracts/users-admin').UsersList
> {
  return useQuery({
    queryKey: ['users-admin'],
    queryFn: async () => {
      const mod = await import('@/api/endpoints/users-admin');
      return mod.listUsersAdmin();
    },
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
}

/** Roles disponibles — para popular dropdown del form de alta. */
export function useRolesAdmin(): UseQueryResult<
  import('@/api/contracts/users-admin').RolesList
> {
  return useQuery({
    queryKey: ['users-admin-roles'],
    queryFn: async () => {
      const mod = await import('@/api/endpoints/users-admin');
      return mod.listRolesAdmin();
    },
    staleTime: 5 * 60_000,
  });
}

/** Cola ANMAT — lista de traza_job (última por referencia+tipo) del GLN. */
export function useAnmatCola(
  query: import('@/api/contracts/anmat-cola').AnmatColaQuery | null,
): UseQueryResult<import('@/api/contracts/anmat-cola').AnmatColaResponse> {
  return useQuery({
    queryKey: [
      'anmat-cola',
      query?.glnId ?? null,
      query?.status ?? null,
      query?.tipo ?? null,
      query?.q ?? null,
    ],
    queryFn: async () => {
      if (!query) throw new Error('query requerida');
      const mod = await import('@/api/endpoints/anmat-cola');
      return mod.listAnmatCola(query);
    },
    enabled: Boolean(query?.glnId),
    refetchInterval: 3000,
    refetchOnMount: 'always',
    staleTime: 1000,
  });
}

/** Mutation: crear usuario. Invalida el listado al success. */
export function useCrearUsuarioMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: import('@/api/contracts/users-admin').CreateUserPayload,
    ) => {
      const mod = await import('@/api/endpoints/users-admin');
      return mod.createUserAdmin(payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users-admin'] });
    },
  });
}

/**
 * Trazabilidad por nº de serie — stock + stock_event timeline.
 */
export function useTrazabilidadPorSerie(
  serie: string | null,
): UseQueryResult<import('@/api/contracts/trazabilidad').TrazabilidadPorSerie> {
  return useQuery({
    queryKey: serie ? ['trazabilidad-serie', serie] : ['trazabilidad-serie', 'idle'],
    queryFn: async () => {
      const mod = await import('@/api/endpoints/trazabilidad');
      if (!serie) throw new Error('serie requerida');
      return mod.getTrazabilidadPorSerie(serie);
    },
    enabled: Boolean(serie),
    staleTime: 30_000,
  });
}

/** Vista previa de un despacho (para el modal de trazabilidad). */
export function useDespachoPreviewTraza(referencia: string | null) {
  return useQuery({
    queryKey: ['traza-despacho-preview', referencia],
    queryFn: async () => {
      const mod = await import('@/api/endpoints/trazabilidad');
      return mod.getDespachoPreview(referencia as string);
    },
    enabled: Boolean(referencia),
    staleTime: 60_000,
    retry: false,
  });
}

/** Vista previa de un ingreso ANMAT (para el modal de trazabilidad). */
export function useIngresoPreviewTraza(anmatTaskId: number | null) {
  return useQuery({
    queryKey: ['traza-ingreso-preview', anmatTaskId],
    queryFn: async () => {
      const mod = await import('@/api/endpoints/trazabilidad');
      return mod.getIngresoPreview(anmatTaskId as number);
    },
    enabled: anmatTaskId != null,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Recepciones completadas (anmat_task con estado='trazado') del GLN activo.
 * Filtros opcionales: búsqueda + rango de fechas sobre `traced_at`.
 */
export function useRecepcionCompletadas(
  glnId: string | number | null,
  filters: import('@/api/contracts/recepcion-completadas').RecepcionCompletadasFilters = {},
): UseQueryResult<import('@/api/contracts/recepcion-completadas').RecepcionCompletadas> {
  return useQuery({
    queryKey: glnId
      ? [
          'recepcion-completadas',
          String(glnId),
          filters.search ?? '',
          filters.dateFrom ?? '',
          filters.dateTo ?? '',
        ]
      : ['recepcion-completadas', 'idle'],
    queryFn: () => {
      if (!glnId) throw new Error('glnId requerido');
      return recepcionRealApi.getRecepcionCompletadas(glnId, filters);
    },
    enabled: Boolean(glnId),
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
}

/**
 * Listado del audit_log (backend real, hash chain). Refresca cada 30s para
 * reflejar eventos recientes (login, traza, etc).
 */
export function useAuditEntries(
  filters: auditLogApi.AuditEntriesFilters = {},
): UseQueryResult<{ entries: import('@/api/contracts/audit-log').AuditEntry[]; limit: number; offset: number }> {
  return useQuery({
    queryKey: queryKeys.auditEntries(filters as Record<string, unknown>),
    queryFn: () => auditLogApi.getAuditEntries(filters),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Verificación on-demand del hash chain. Disabled por default — la dispara
 * el usuario clickeando un botón, no es una query automática.
 */
export function useVerifyAuditChain(): UseQueryResult<
  import('@/api/contracts/audit-log').VerifyChainResponse
> {
  return useQuery({
    queryKey: queryKeys.auditVerify,
    queryFn: () => auditLogApi.verifyAuditChain(),
    enabled: false,
  });
}

/**
 * Registro de diagnóstico de una lectura del escáner del pickeo
 * (POST /api/pickeo-log/lectura). Fire-and-forget: no invalida nada y un
 * fallo solo deja un warning en consola; nunca condiciona el pickeo.
 */
export function useRegistrarLecturaPickeoMutation() {
  return useMutation({
    mutationFn: (input: pickeoLogApi.RegistrarLecturaInput) => pickeoLogApi.registrarLectura(input),
    onError: (err) => {
      console.warn('[pickeo-log] no se pudo registrar la lectura:', err);
    },
  });
}
